import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  ArrowUpRight,
  BookOpen,
  Boxes,
  Building2,
  Cable,
  ChevronRight,
  CreditCard,
  FileCode2,
  Home,
  FolderLock,
  Layers3,
  LogOut,
  Menu,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { Brand } from "./Navbar";
import { Dialog } from "./Dialog";
import { clearToken } from "../lib/auth";
import { platformAccess } from "../lib/api/platform";
import { useWorkspace } from "../lib/workspace";

export function AppShell({
  children,
  fullBleed = false,
}: {
  children: ReactNode;
  fullBleed?: boolean;
}) {
  const {
    workspace,
    customer,
    orgs,
    loading,
    error,
    href,
    refresh,
    selectWorkspace,
  } = useWorkspace();
  const location = useLocation();
  const [mobile, setMobile] = useState(false);
  const [support, setSupport] = useState(false);
  useEffect(() => {
    setMobile(false);
  }, [location.pathname, location.search]);
  useEffect(() => {
    let live = true;
    setSupport(false);
    if (customer)
      void platformAccess()
        .then((result) => {
          if (live) setSupport(result.eligible);
        })
        .catch(() => {});
    return () => {
      live = false;
    };
  }, [customer?.id]);
  const groups = [
    {
      label: "Workspace",
      items: [
        { to: href("/servers"), label: "Overview", icon: Home },
        {
          to: workspace?.org_id ? `/orgs/${workspace.org_id}/access` : "/orgs",
          label: "Projects & teams",
          icon: FolderLock,
        },
        {
          to: href("/connections"),
          label: "Connections",
          icon: Cable,
        },
        { to: href("/surfaces"), label: "Surfaces", icon: Boxes },
        {
          to: href("/catalogue"),
          label: "Explore templates",
          icon: Layers3,
        },
      ],
    },
    {
      label: "Build & operate",
      items: [
        { to: href("/skills"), label: "Skills", icon: Sparkles },
        {
          to: href("/approvals"),
          label: "Approvals",
          icon: ShieldCheck,
        },
        { to: href("/workflows"), label: "Workflows", icon: Activity },
      ],
    },
    {
      label: "Manage",
      items: [
        {
          to: workspace?.org_id
            ? `/orgs/${workspace.org_id}?tab=members`
            : "/orgs",
          label: workspace?.org_id ? "People & access" : "Organizations",
          icon: Users,
        },
        {
          to: workspace?.org_id
            ? `/orgs/${workspace.org_id}?tab=security`
            : "/settings/security",
          label: workspace?.org_id ? "Workspace security" : "Account security",
          icon: ShieldCheck,
        },
        {
          to: workspace?.org_id
            ? `/orgs/${workspace.org_id}/billing`
            : "/billing",
          label: "Usage & billing",
          icon: CreditCard,
        },
      ],
    },
  ];
  const active = (to: string) => {
    const url = new URL(to, "https://pugio.invalid");
    if (url.pathname !== location.pathname) return false;
    const tab = url.searchParams.get("tab");
    return (
      !tab ||
      (new URLSearchParams(location.search).get("tab") || "members") === tab
    );
  };
  const navigation = (
    <>
      <div className="mb-6 px-2">
        <Link to={href("/servers")} aria-label="Pugio home">
          <Brand />
        </Link>
      </div>
      <label className="workspace-picker">
        <span className="sr-only">Active workspace</span>
        <Building2 size={18} aria-hidden="true" />
        <select
          value={workspace?.id ?? ""}
          aria-label="Active workspace"
          onChange={(event) => selectWorkspace(event.target.value)}
          disabled={loading}
        >
          {!workspace && <option value="">Choose a workspace</option>}
          <option value="personal">Personal workspace</option>
          {orgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
      </label>
      {workspace && (
        <div className="mb-5 mt-2 flex items-center justify-between px-2 text-xs text-pugio-muted">
          <span className="capitalize">
            {workspace.org_id ? workspace.role : "Only you"}
          </span>
          <span className="plan-badge">{workspace.plan_name}</span>
        </div>
      )}
      <nav aria-label="Workspace navigation" className="space-y-5">
        {groups.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (!workspace) return true;
            if (item.label === "Usage & billing" && workspace.org_id)
              return workspace.permissions.manage_billing;
            if (item.label === "Workspace security")
              return workspace.permissions.manage_members;
            if (item.label === "Connect an API" && workspace.org_id && !workspace.project_id && workspace.role === "member") return false;
            if (item.label === "Connect an API" && workspace.project_id)
              return workspace.project_permissions.includes("servers.edit");
            const resource = (
              {
                Capabilities: "capabilities",
                "Surfaces": "capabilities",
                Connections: "connections",
                Skills: "skills",
                Workflows: "workflows",
              } as Record<string, string>
            )[item.label];
            return (
              !resource ||
              !workspace.project_id ||
              workspace.project_permissions.includes(`${resource}.read`)
            );
          });
          if (!visibleItems.length) return null;
          const links = visibleItems.map(({ to, label, icon: Icon }) => (
            <Link
              key={label}
              to={to}
              className={`side-link ${active(to) ? "is-active" : ""}`}
              aria-current={active(to) ? "page" : undefined}
            >
              <Icon size={17} strokeWidth={1.8} aria-hidden="true" />
              {label}
            </Link>
          ));
          return group.label === "Build & operate" ? (
            <details
              key={`${workspace?.id}:${group.label}`}
              open={group.items.some((item) => active(item.to))}
            >
              <summary className="nav-group-label cursor-pointer py-2">
                {group.label}
              </summary>
              {links}
            </details>
          ) : (
            <div key={group.label}>
              <p className="nav-group-label">{group.label}</p>
              {links}
            </div>
          );
        })}
      </nav>
      <div className="mt-auto space-y-1 border-t border-pugio-border pt-4">
        <Link className="side-link" to={href("/harness")}>
          <FileCode2 size={17} />
          Connect your AI client
        </Link>
        <Link className="side-link" to="/orgs">
          <Building2 size={17} />
          All workspaces
        </Link>
        {support && (
          <Link className="side-link" to="/platform">
            <ShieldCheck size={17} />
            Platform support
          </Link>
        )}
        <Link className="side-link" to="/settings/security">
          <span className="avatar">
            {customer?.email?.[0]?.toUpperCase() || "P"}
          </span>
          <span className="min-w-0 truncate">
            {customer?.email || "Your account"}
          </span>
        </Link>
        <button
          className="side-link w-full"
          type="button"
          onClick={() => {
            clearToken();
            window.location.assign("/");
          }}
        >
          <LogOut size={17} />
          Sign out
        </button>
      </div>
    </>
  );
  return (
    <div className="app-shell">
      <a className="skip-link" href="#workspace-main">
        Skip to content
      </a>
      <aside className="app-sidebar">{navigation}</aside>
      <Dialog open={mobile} title="Navigation" onClose={() => setMobile(false)}>
        <div className="flex flex-col gap-3">{navigation}</div>
      </Dialog>
      <div className="app-content">
        <header className="app-topbar">
          <button
            type="button"
            className="icon-button lg:hidden"
            aria-label="Open navigation"
            onClick={() => setMobile(true)}
          >
            <Menu size={20} />
          </button>
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <span className="truncate font-medium">
              {workspace?.name || "Pugio"}
              {workspace?.project_name && (
                <span className="ml-2 font-normal text-pugio-muted">
                  / {workspace.project_name}
                </span>
              )}
            </span>
            <ChevronRight
              size={14}
              className="hidden shrink-0 text-pugio-muted sm:block"
            />
            <span className="hidden shrink-0 text-pugio-muted sm:block">
              Workspace
            </span>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <Link
              to="/docs/quickstart"
              aria-label="Help"
              className="inline-flex items-center gap-1 text-sm text-pugio-muted"
            >
              <BookOpen size={16} />
              <span className="hidden sm:inline">Help</span>
            </Link>
            <Link className="btn-primary text-xs" to={href("/pricing")}>
              View plans <ArrowUpRight size={14} />
            </Link>
          </div>
        </header>
        <main
          id="workspace-main"
          className={
            fullBleed ? "workspace-main workspace-main-wide" : "workspace-main"
          }
        >
          {customer?.mfa_enrollment_deadline_at && (
            <div className="notice-info mb-5 flex flex-wrap items-center justify-between gap-3" role="status">
              <p>Your organization requires two-factor authentication. Set it up by {new Date(customer.mfa_enrollment_deadline_at).toLocaleString()} to keep access.</p>
              <Link className="shrink-0 font-semibold underline" to="/settings/security#two-factor">Set up authenticator</Link>
            </div>
          )}
          {loading ? (
            <div className="panel p-8 text-sm text-pugio-muted" role="status">
              Loading your workspace…
            </div>
          ) : error ? (
            <div className="panel space-y-4 p-8">
              <h1 className="text-xl font-semibold">Workspace unavailable</h1>
              <p role="alert" className="text-pugio-muted">
                {error}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  className="btn-primary"
                  onClick={() => selectWorkspace("personal")}
                >
                  Open personal workspace
                </button>
                <button className="btn-ghost" onClick={refresh}>
                  Try again
                </button>
              </div>
            </div>
          ) : (
            children
          )}
        </main>
      </div>
    </div>
  );
}
